// Image that gracefully degrades when the src is missing or fails to load.
// Lazy-loaded by default; pass loading="eager" for above-the-fold.
//
// `fallback` lets a caller pass any node (text, icon). When omitted we
// render the shared placeholder SVG from src/assets, which already
// inherits CSS-variable colours so it tints with the active industry.
import { useEffect, useState } from 'react';
import { assets } from '@/assets';

export default function ImageWithFallback({
  src,
  alt = '',
  aspectRatio = '1 / 1',
  radius = 'var(--radius-card, 0.5rem)',
  loading = 'lazy',
  decoding = 'async',
  fit = 'cover',
  fallback,
  fallbackSrc,
  className,
  style,
  ...rest
}) {
  const [failed, setFailed] = useState(false);
  const resolvedSrc = typeof src === 'string' ? src.trim() : src;

  useEffect(() => {
    setFailed(false);
  }, [resolvedSrc]);

  const showFallback = !resolvedSrc || failed;

  if (showFallback) {
    if (fallback !== undefined) {
      return (
        <div
          className={className}
          style={{
            aspectRatio,
            background: 'var(--bg-surface)',
            borderRadius: radius,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-secondary)',
            fontSize: 14,
            ...style,
          }}
          role={alt ? 'img' : undefined}
          aria-label={alt || undefined}
        >
          {fallback}
        </div>
      );
    }
    return (
      <img
        src={fallbackSrc || assets.placeholderProduct}
        alt={alt}
        loading={loading}
        decoding={decoding}
        className={className}
        style={{
          aspectRatio,
          width: '100%',
          objectFit: fit,
          borderRadius: radius,
          display: 'block',
          background: 'var(--bg-surface)',
          ...style,
        }}
        {...rest}
      />
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      loading={loading}
      decoding={decoding}
      onError={() => setFailed(true)}
      className={className}
      style={{
        aspectRatio,
        width: '100%',
        objectFit: fit,
        borderRadius: radius,
        display: 'block',
        ...style,
      }}
      {...rest}
    />
  );
}
